import { FC, useEffect, useRef } from "react";

import Hls from "hls.js";

import { videoPlayerVariable } from "../../../../../../common/variables/videoPlayer";

interface LiveStreamPlayerProps {
  streamUrl: string;
  setVideoError?: () => void;
  onAspectRatio?: (ratio: number) => void;
  showControls?: boolean;
  isLive?: boolean;
}

const LiveStreamPlayer: FC<LiveStreamPlayerProps> = ({
  streamUrl,
  setVideoError,
  onAspectRatio,
  showControls = false,
  isLive = true,
}): JSX.Element => {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    let hls: Hls | null = null;
    let handleSeeking: (() => void) | null = null;
    let handleVisibilityChange: (() => void) | null = null;

    if (!videoRef.current) {
      return;
    }

    const handleLoadedMetadata = (): void => {
      if (!videoRef.current) return;

      const width = videoRef.current.videoWidth || 0;
      const height = videoRef.current.videoHeight || 0;

      if (width > 0 && height > 0) {
        const ratio = width / height;

        if (onAspectRatio) onAspectRatio(ratio);
      }
    };

    if (Hls.isSupported()) {
      hls = new Hls({
        liveSyncDuration: 3,
        liveBackBufferLength: 3,
        maxBufferLength: 50,
        maxMaxBufferLength: 60,
        liveDurationInfinity: true,
      });

      hls.loadSource(streamUrl);

      hls.attachMedia(videoRef.current);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        videoRef.current
          ?.play()
          .then(() => {
            videoPlayerVariable(true);
          })
          .catch(error => {
            console.error("Error playing video:", error);
          });
      });

      videoRef.current.addEventListener("loadedmetadata", handleLoadedMetadata);

      handleVisibilityChange = () => {
        if (!videoRef.current) return;

        if (document.visibilityState === "hidden") {
          videoRef.current.pause();
        } else {
          videoRef.current.play().catch(error => {
            console.error("Error playing video:", error);
          });
        }
      };

      if (videoRef.current) {
        document.addEventListener("visibilitychange", handleVisibilityChange);
      }

      handleSeeking = () => {
        if (!isLive || !videoRef.current || !hls) return;

        const liveEdge = hls.liveSyncPosition ?? videoRef.current.duration;

        if (liveEdge !== Infinity && videoRef.current.currentTime < liveEdge) {
          videoRef.current.currentTime = liveEdge;
        }
      };

      if (isLive) {
        videoRef.current.addEventListener("seeking", handleSeeking);
      }
    } else {
      if (videoRef.current.canPlayType("application/vnd.apple.mpegurl")) {
        videoRef.current.src = streamUrl;

        videoRef.current.addEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );

        videoRef.current.addEventListener("loadedmetadata", () => {
          videoRef.current
            ?.play()
            .then(() => {
              videoPlayerVariable(true);
            })
            .catch(error => {
              console.error("Error playing video:", error);
            });
        });
      }
    }

    return () => {
      if (handleVisibilityChange) {
        document.removeEventListener(
          "visibilitychange",
          handleVisibilityChange
        );
      }

      if (videoRef.current) {
        videoRef.current.removeEventListener(
          "loadedmetadata",
          handleLoadedMetadata
        );

        if (handleSeeking) {
          videoRef.current.removeEventListener("seeking", handleSeeking);
        }
      }

      if (hls) {
        hls.destroy();
      }
    };
  }, [streamUrl, isLive]);

  const handleOnError = (): void => {
    if (setVideoError) {
      setVideoError();
    }
  };

  return (
    <video
      ref={videoRef}
      autoPlay
      muted
      playsInline
      style={{
        width: "100%",
        height: "100%",
        objectFit: "contain",
        objectPosition: "center",
        backgroundColor: "#000",
      }}
      onError={handleOnError}
      controls={showControls}
    >
      Your browser does not support the video tag.
    </video>
  );
};

export default LiveStreamPlayer;
